import { useSetAtom } from 'jotai';
import { cloneDeep } from 'lodash-es';
import { useCallback } from 'react';
import { fundFilterStateAtom } from '../../../components/Layout/Navbar/Navigation/PortfolioReporting/FundNavigation/FundFilterSingleSelect';
import { ViewConfigDataModel } from '../../../data-models/view-config.data-model';
import { IViewConfig2 } from '../../../data-models/view.data-model';
import { serializeView, ViewModel } from '../../../models/view.model';
import { useUpdateSelectedView } from '../hooks/useViewActions';
import { createMprViewSettings } from './MultiPeriodReporting.schemas';
import { getColumnConfigKey, usePreprocessMprViewSettings } from './multiPeriodReportingUtils';
import { MprCompanyColId } from './multiperiodReportingGridData';
import { savedMprViewConfigAtom } from './useMprColumnConfigs';

export function useInitializeMprView(selectedView: ViewModel) {
  const view = serializeView(selectedView);
  const setViewConfig = useSetAtom(savedMprViewConfigAtom(view.id));
  const preprocessMprViewSettings = usePreprocessMprViewSettings();
  const syncFundFilterWithView = useSyncFundFilterWithView();

  const { multiPeriodReportingConfig, gridState } = view.config ?? {};
  return useCallback(() => {
    if (!view?.id) {
      return { initialized: false };
    }
    const preprocessedConfig = multiPeriodReportingConfig
      ? preprocessMprViewSettings({ ...multiPeriodReportingConfig })
      : null;
    setViewConfig((prev) => {
      if (prev) {
        return prev;
      }
      if (!multiPeriodReportingConfig) {
        return createMprViewSettings();
      } else {
        return preprocessedConfig;
      }
    });
    const initialState = { ...cloneDeep(gridState), partialColumnState: true };
    if (
      multiPeriodReportingConfig?.columnSort &&
      initialState?.columnOrder?.orderedColIds &&
      preprocessedConfig
    ) {
      initialState.columnOrder!.orderedColIds = [MprCompanyColId].concat(
        preprocessedConfig.columns.map((kpi) => getColumnConfigKey(kpi))
      );
    }
    syncFundFilterWithView(view.config);
    return { initialized: true, initialState };
  }, [
    gridState,
    multiPeriodReportingConfig,
    preprocessMprViewSettings,
    setViewConfig,
    syncFundFilterWithView,
    view.config,
    view?.id,
  ]);
}

/** Set fundFilterStateAtom based on viewConfig once during view initialization;
 * after that always read state from atom to avoid unnecessary re-renders */
export function useSyncFundFilterWithView() {
  const setFundId = useSetAtom(fundFilterStateAtom);

  return useCallback(
    (viewConfig: ViewConfigDataModel) => {
      if (viewConfig.fundId != null && viewConfig.fundId !== 0) {
        setFundId(viewConfig.fundId);
      } else {
        setFundId(null);
      }
    },
    [setFundId]
  );
}

export function useSaveMprStateInView() {
  const updateSelectedView = useUpdateSelectedView();

  return useCallback(
    async ({ gridState, multiPeriodReportingConfig }: IViewConfig2) => {
      if (!gridState && !multiPeriodReportingConfig) {
        return;
      }
      const updated: IViewConfig2 = {};
      if (gridState) {
        updated.gridState = gridState;
      }
      if (multiPeriodReportingConfig) {
        updated.multiPeriodReportingConfig = multiPeriodReportingConfig;
      }
      updateSelectedView(updated);
    },
    [updateSelectedView]
  );
}
